import { useQuery } from "@tanstack/react-query"
import { authFetchJson } from "@/shared/lib/auth-fetch"
import { HOME } from "@/shared/constants/backend-api"

export interface LightweightHome {
  id: number
  address: string
  latitude: number | null
  longitude: number | null
  user: {
    fullname: string
    phone: string
  }
}

export interface LightweightHomesResponse {
  success: boolean
  data: LightweightHome[]
  message?: string
  error?: string
}

/**
 * Hook to fetch lightweight home data for dropdowns and lists
 * Returns only essential fields: id, address, latitude, longitude, user fullname, user phone
 * 
 * @param regionId - Optional region ID to filter homes by region
 * @returns {Object} {
 *  data: LightweightHomesResponse,
 *  isLoading: boolean,
 *  error: Error | null
 * }
 */
type UseLightweightHomesOptions = {
  enabled?: boolean
  /** Include inactive homes; defaults to active homes only. */
  includeInactive?: boolean
}

export function useLightweightHomes(regionId?: number, options: UseLightweightHomesOptions = {}) {
  const { enabled = true, includeInactive = false } = options
  const query = useQuery({
    queryKey: ['homes', 'lightweight', regionId, includeInactive],
    queryFn: async (): Promise<LightweightHomesResponse> => {
      const params = new URLSearchParams()
      if (regionId) params.set('regionId', regionId.toString())
      if (includeInactive) params.set('includeInactive', 'true')
      const url = `${HOME.base}/lightweight${params.toString() ? `?${params.toString()}` : ''}`
      const res = await authFetchJson<LightweightHome[]>(url)
      if (!res.success) {
        throw new Error(res.message || 'Failed to fetch lightweight homes')
      }
      return {
        success: true,
        data: res.data
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    enabled,
  })

  return query
}
